import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Issue, Person, TargetKind } from './game/types';
import { makeKey } from './game/types';
import { grade } from './game/scoring';
import { sprint42 } from './scenarios/sprint42';
import { levelsForTool, toolProgress } from './scenarios';
import type { ToolId } from './game/partners';
import { partnerForTool, toolById } from './game/partners';
import type { ImageScenario } from './game/imageTypes';
import type { Progress } from './game/progress';
import { clearProgress, loadProgress, recordLevelResult } from './game/progress';
import { BrandHeader } from './components/BrandHeader';
import { RTNav } from './components/RTNav';
import { SiteFooter } from './components/SiteFooter';
import { Board } from './components/Board';
import { Briefing } from './components/Briefing';
import { DetailSheet } from './components/DetailSheet';
import { ImageGame } from './components/ImageGame';
import { Results } from './components/Results';
import { SignIn } from './components/SignIn';
import { CaseCard } from './components/CaseCard';
import { BackButton } from './components/BackButton';
import { ConfirmLeave } from './components/ConfirmLeave';
import { Splash } from './components/Splash';
import { ToolPicker } from './components/ToolPicker';
import { PrivacyNotice } from './components/PrivacyNotice';
import { AttributionNotice } from './components/AttributionNotice';
import { Leaderboard } from './components/Leaderboard';
import { AdminMenu } from './components/AdminMenu';
import { checkAdmin } from './game/admin';
import { loadRuntimeContent } from './game/contentStore';
import type { Player } from './game/identity';
import {
  clearPlayer,
  eraseMyData,
  loadPlayer,
  myDataDownloadUrl,
  resumeSession,
} from './game/identity';

type Phase = 'briefing' | 'playing' | 'results';

/**
 * Where the player is.
 *
 * `splash` and `cases` are public. Identity is only required to START a case,
 * which is the first moment a score exists to attach to anyone — see `pending`.
 */
type Mode = 'splash' | 'picker' | 'cases' | 'image' | 'board' | 'admin';

/**
 * A case the player asked for but is not signed in for yet.
 *
 * Held rather than discarded so that signing in resumes what they were doing.
 * Sending someone back to the menu after they have just proved their email is
 * how you make a verification step feel like a punishment.
 */
type Pending =
  | { kind: 'image'; level: ImageScenario }
  | { kind: 'board' }
  /**
   * Signing in for its own sake, not on the way to a case.
   *
   * Needed because sign-in is otherwise only reachable by starting a level.
   * That left someone who had signed in before with no way to prove it again,
   * and an admin with no way to reach the admin menu at all.
   */
  | { kind: 'signin' };

const scenario = sprint42;

export function App() {
  const [player, setPlayer] = useState<Player | null>(() => loadPlayer());
  const [progress, setProgress] = useState<Progress>(() => loadProgress());
  const [mode, setMode] = useState<Mode>('splash');
  const [toolId, setToolId] = useState<ToolId | null>(null);
  const [level, setLevel] = useState<ImageScenario | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [phase, setPhase] = useState<Phase>('briefing');
  const [flagged, setFlagged] = useState<ReadonlySet<string>>(() => new Set());
  const [detailKey, setDetailKey] = useState<string | null>(null);
  /** Two-step confirm on the reset, because wiping scores cannot be undone. */
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [confirmingErase, setConfirmingErase] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  /**
   * Whether the SERVER says this session may edit content.
   *
   * Only ever used to decide whether to show the menu. Every admin route
   * re-checks; this flag grants nothing.
   */
  const [isAdmin, setIsAdmin] = useState(false);
  /**
   * Bumped whenever the runtime content layer is reloaded.
   *
   * levelsForTool() and friends read a module-level store, so React needs
   * something to re-render on. This is that something.
   */
  const [contentVersion, setContentVersion] = useState(0);
  /* The privacy notice lives on a hash so it can be linked to from anywhere,
     including from outside the app. */
  const [hash, setHash] = useState(() => window.location.hash);

  const issuesByKey = useMemo(() => {
    const map = new Map<string, Issue>();
    for (const issue of scenario.issues) map.set(issue.key, issue);
    return map;
  }, []);

  const peopleById = useMemo(() => {
    const map = new Map<string, Person>();
    for (const person of scenario.people) map.set(person.id, person);
    return map;
  }, []);

  const toggleFlag = useCallback((kind: TargetKind, id: string) => {
    const key = makeKey(kind, id);
    setFlagged((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const result = useMemo(() => grade(scenario, flagged), [flagged]);

  const restart = useCallback(() => {
    setFlagged(new Set());
    setDetailKey(null);
    setPhase('briefing');
  }, []);

  /**
   * Confirm a stored session is still valid.
   *
   * Runs once on load. A token that expired, or whose account was erased, must
   * sign the player out rather than leave a stale identity on screen claiming
   * to be signed in.
   */
  useEffect(() => {
    let cancelled = false;
    void resumeSession().then((confirmed) => {
      if (cancelled) return;
      setPlayer(confirmed);
    });
    return () => {
      cancelled = true;
    };
    // Deliberately once: re-running on every player change would re-check the
    // session immediately after signing in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Pull the runtime content layer once at load.
   *
   * Failure is silent on purpose: the compiled levels are a complete campaign,
   * and a player should never see an error because an admin feature is down.
   */
  useEffect(() => {
    void loadRuntimeContent().then(() => setContentVersion((v) => v + 1));
  }, []);

  /** Ask the server whether this session is an admin, whenever it changes. */
  useEffect(() => {
    let cancelled = false;
    if (!player) {
      setIsAdmin(false);
      return;
    }
    void checkAdmin().then((allowed) => {
      if (!cancelled) setIsAdmin(allowed);
    });
    return () => {
      cancelled = true;
    };
  }, [player]);

  const reloadContent = useCallback(async () => {
    await loadRuntimeContent();
    setContentVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const backToCases = useCallback(() => {
    setFlagged(new Set());
    setDetailKey(null);
    setPhase('briefing');
    setConfirmingLeave(false);
    setMode(toolId ? 'cases' : 'picker');
  }, [toolId]);

  /**
   * Leave the board, checking first only if there is something to lose.
   *
   * Mirrors the image game: an untouched board is not worth interrupting over,
   * and a confirm that always fires is one people learn to dismiss unread.
   */
  const leaveBoard = useCallback(() => {
    if (phase === 'playing' && flagged.size > 0) {
      setConfirmingLeave(true);
      return;
    }
    backToCases();
  }, [phase, flagged.size, backToCases]);

  /**
   * Open a case, asking for identity only if we do not have it.
   *
   * The check lives here rather than in the menu components so there is exactly
   * one place that decides when a stranger becomes a player.
   */
  const openCase = useCallback(
    (next: Pending) => {
      if (!player) {
        setPending(next);
        return;
      }
      if (next.kind === 'image') {
        setLevel(next.level);
        setMode('image');
      } else {
        setPhase('briefing');
        setMode('board');
      }
    },
    [player],
  );

  const showLeaderboard = useCallback(() => {
    window.location.hash = 'leaderboard';
    setHash('#leaderboard');
  }, []);

  const closeLeaderboard = useCallback(() => {
    // Replace rather than push, so closing does not leave an entry that the
    // back button would walk straight back into.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setHash('');
  }, []);

  const showPrivacy = useCallback(() => {
    window.location.hash = 'privacy';
    setHash('#privacy');
  }, []);

  /*
   * The screen under any overlay.
   *
   * Wrapped in a function so the leaderboard can float ON TOP of whatever the
   * player was looking at, rather than replacing it. The chain still returns
   * early exactly as before; only its result is captured rather than returned.
   */
  const screen = (() => {
  if (hash === '#attribution') {
    return (
      <AttributionNotice
        onBack={() => {
          window.location.hash = '';
          setHash('');
        }}
      />
    );
  }

  if (hash === '#privacy') {
    return (
      <PrivacyNotice
        onBack={() => {
          window.location.hash = '';
          setHash('');
        }}
      />
    );
  }

  // Sign-in is an interruption now, not a front door: it appears only once
  // somebody has chosen a case, and it hands them straight back to it.
  if (pending && !player) {
    return (
      <SignIn
        {...(pending.kind === 'image'
          ? { pendingTitle: pending.level.title }
          : pending.kind === 'board'
            ? { pendingTitle: scenario.title }
            : {})}
        onCancel={() => setPending(null)}
        onShowPrivacy={showPrivacy}
        onSignedIn={(confirmed) => {
          setPlayer(confirmed);
          if (pending.kind === 'image') {
            setLevel(pending.level);
            setMode('image');
          } else if (pending.kind === 'board') {
            setPhase('briefing');
            setMode('board');
          }
          // 'signin' leaves them exactly where they were, now identified.
          setPending(null);
        }}
      />
    );
  }

  if (mode === 'splash') {
    return (
      <Splash
        onStart={() => setMode('picker')}
        player={player}
        onSignIn={() => setPending({ kind: 'signin' })}
        onSignOut={() => {
          clearPlayer();
          setPlayer(null);
        }}
        onLeaderboard={showLeaderboard}
        {...(isAdmin ? { onAdmin: () => setMode('admin') } : {})}
      />
    );
  }

  if (mode === 'admin' && isAdmin) {
    return (
      <AdminMenu
        onContentChanged={reloadContent}
        {...(toolId ? { currentToolId: toolId } : {})}
        onBack={() => setMode(toolId ? 'cases' : 'picker')}
      />
    );
  }

  if (mode === 'picker') {
    return (
      <ToolPicker
        key={contentVersion}
        player={player}
        onSignIn={() => setPending({ kind: 'signin' })}
        onSignOut={() => {
          clearPlayer();
          setPlayer(null);
        }}
        progress={progress}
        onPick={(picked) => {
          setToolId(picked);
          setMode('cases');
        }}
        onBack={() => setMode('splash')}
        {...(isAdmin ? { onAdmin: () => setMode('admin') } : {})}
      />
    );
  }

  if (mode === 'cases' && toolId) {
    const tool = toolById(toolId);
    const partner = partnerForTool(toolId);
    const cases = levelsForTool(toolId);
    const { ready, target } = toolProgress(toolId);
    // The rendered-board level belongs to whichever tool it recreates, so it
    // follows the data rather than being pinned to Jira in the markup.
    const showBoardExtra = scenario.source.toolId === toolId;

    return (
      <div className="panel panel--menu">
        <RTNav />
        <div className="panel__inner">
          <BrandHeader />

          <p className="case-eyebrow" style={{ color: partner.accent }}>
            {partner.name}
          </p>
          <h1>{tool.short}</h1>
          <p className="lede">{tool.blurb}</p>

          {player && (
            <p className="identity-bar">
              <span>
                Playing as <strong>{player.name}</strong> ({player.email})
              </span>
              <button
                type="button"
                onClick={() => {
                  clearPlayer();
                  setPlayer(null);
                }}
              >
                Not you?
              </button>
            </p>
          )}

          <h2>
            Cases — {ready} of {target} ready
          </h2>
          {cases.map((entry, index) => (
            <CaseCard
              key={entry.id}
              scenario={entry}
              number={index + 1}
              best={progress[entry.id]?.bestScore}
              onOpen={() => openCase({ kind: 'image', level: entry })}
            />
          ))}

          {ready < target && (
            <p className="fineprint">
              {target - ready} more cases are waiting on screenshots. Each one is
              a data file — no code changes needed.
            </p>
          )}

          {showBoardExtra && (
            <>
              <h2>Also</h2>
              <button
                type="button"
                className="menu-card"
                onClick={() => openCase({ kind: 'board' })}
              >
                <b>{scenario.title}</b>
                <span>
                  {tool.short} · {scenario.subtitle} — {scenario.defects.length}{' '}
                  problems planted across a fully rendered board.
                </span>
              </button>
            </>
          )}

          <button
            type="button"
            className="btn btn--ghost btn--wide"
            style={{ marginTop: 24 }}
            onClick={() => setMode('picker')}
          >
            All tools
          </button>

          {/* Shown only to a session the server confirmed is an admin. Hiding
              it is presentation; the routes behind it check for themselves. */}
          {isAdmin && (
            <button
              type="button"
              className="btn btn--ghost btn--wide admin-entry"
              onClick={() => setMode('admin')}
            >
              Level admin
            </button>
          )}

          {player && (
            <>
              {Object.keys(progress).length > 0 && (
                <div className="reset">
                  {confirmingReset ? (
                    <>
                      <p className="fineprint" style={{ marginBottom: 10 }}>
                        This clears your best scores on this device, for every
                        tool. Results already submitted are kept, and it cannot
                        be undone.
                      </p>
                      <div className="reset__actions">
                        <button
                          type="button"
                          className="btn btn--danger"
                          onClick={() => {
                            clearProgress();
                            setProgress({});
                            setConfirmingReset(false);
                          }}
                        >
                          Yes, reset my scores
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => setConfirmingReset(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="reset__trigger"
                      onClick={() => setConfirmingReset(true)}
                    >
                      Reset my scores
                    </button>
                  )}
                </div>
              )}

              <div className="reset">
                {confirmingErase ? (
                  <>
                    <p className="fineprint" style={{ marginBottom: 10 }}>
                      This deletes your name, email and every score we hold for{' '}
                      <strong>{player.email}</strong>, on the server as well as
                      this device, and invalidates any result you have shared. It
                      happens immediately and cannot be undone.
                    </p>
                    <div className="reset__actions">
                      <button
                        type="button"
                        className="btn btn--danger"
                        onClick={() => {
                          // Local state is cleared regardless of the server's
                          // answer: the person asked to be forgotten, and
                          // leaving their identity in the browser because a
                          // request failed would be the wrong way to fail.
                          void eraseMyData();
                          clearProgress();
                          clearPlayer();
                          setProgress({});
                          setConfirmingErase(false);
                          setPlayer(null);
                        }}
                      >
                        Yes, delete everything
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setConfirmingErase(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <p className="privacy-links">
                    <button
                      type="button"
                      className="reset__trigger"
                      onClick={() => setConfirmingErase(true)}
                    >
                      Delete my data
                    </button>
                    <span aria-hidden="true"> · </span>
                    <a href={myDataDownloadUrl()} download>
                      Download my data
                    </a>
                    <span aria-hidden="true"> · </span>
                    <a href="#privacy">Privacy notice</a>
                  </p>
                )}
              </div>
            </>
          )}

          <SiteFooter />
        </div>
      </div>
    );
  }

  if (mode === 'image' && level) {
    const siblings = levelsForTool(level.source.toolId);
    const levelIndex = siblings.findIndex((entry) => entry.id === level.id);
    const nextLevel = levelIndex >= 0 ? siblings[levelIndex + 1] : undefined;

    return (
      <ImageGame
        // Keyed so advancing a level remounts the game. Swapping the scenario
        // prop alone would carry the previous level's selections and results
        // across into the new one.
        key={level.id}
        scenario={level}
        levelNumber={levelIndex + 1}
        levelCount={siblings.length}
        nextLevel={nextLevel}
        onNextLevel={() => {
          if (nextLevel) setLevel(nextLevel);
        }}
        onScored={(score) => setProgress((p) => recordLevelResult(p, level.id, score))}
        onExit={backToCases}
      />
    );
  }

  // Any state that is not a recognised screen falls back to the splash rather
  // than dropping the player into the board, which is what an unguarded
  // fallthrough used to do.
  if (mode !== 'board') {
    return (
      <Splash
        onStart={() => setMode('picker')}
        player={player}
        onSignIn={() => setPending({ kind: 'signin' })}
        onSignOut={() => {
          clearPlayer();
          setPlayer(null);
        }}
        onLeaderboard={showLeaderboard}
        {...(isAdmin ? { onAdmin: () => setMode('admin') } : {})}
      />
    );
  }

  // The rendered-board level. Reached only through openCase, so a player exists.
  if (phase === 'briefing') {
    return (
      <Briefing
        scenario={scenario}
        onStart={() => setPhase('playing')}
        onBack={backToCases}
      />
    );
  }

  const detailIssue = detailKey ? issuesByKey.get(detailKey) : undefined;
  const detailColumn = detailKey
    ? scenario.columns.find((c) => c.issueKeys.includes(detailKey))
    : undefined;

  return (
    <div className="app">
      <header className="topbar">
        <BackButton onClick={leaveBoard} label="Leave this case" />
        <img
          className="topbar__logo"
          src="/brand/releaseteam-logo-white.png"
          alt="ReleaseTEAM"
          width={301}
          height={66}
        />
        <span className="topbar__divider" aria-hidden="true" />
        <span className="topbar__text">
          <span className="topbar__title">{scenario.title}</span>
          <span className="topbar__sub">{scenario.subtitle}</span>
        </span>
      </header>

      <Board
        scenario={scenario}
        flagged={flagged}
        onToggleFlag={toggleFlag}
        onOpenDetail={setDetailKey}
      />

      <div className="hud">
        <span className="hud__count">
          <strong>{flagged.size}</strong>
          flagged
        </span>
        <span className="hud__spacer" />
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => setFlagged(new Set())}
          disabled={flagged.size === 0}
        >
          Clear
        </button>
        <button
          type="button"
          className="btn"
          onClick={() => setPhase('results')}
          disabled={flagged.size === 0}
        >
          Submit
        </button>
      </div>

      {detailIssue && (
        <DetailSheet
          issue={detailIssue}
          assignee={
            detailIssue.assigneeId ? peopleById.get(detailIssue.assigneeId) : undefined
          }
          statusName={detailColumn?.name ?? 'Unknown'}
          flagged={flagged.has(makeKey('issue', detailIssue.key))}
          onToggleFlag={() => toggleFlag('issue', detailIssue.key)}
          onClose={() => setDetailKey(null)}
        />
      )}

      {confirmingLeave && (
        <ConfirmLeave
          count={flagged.size}
          onLeave={backToCases}
          onStay={() => setConfirmingLeave(false)}
        />
      )}

      {phase === 'results' && (
        <Results
          scenario={scenario}
          result={result}
          onPlayAgain={restart}
          onReviewBoard={() => setPhase('playing')}
          onExit={backToCases}
        />
      )}
    </div>
  );
  })();

  return (
    <>
      {screen}
      {/*
        On a hash so the phone's back button closes it, alongside Escape, a tap
        on the backdrop, and the close button. It floats over the screen rather
        than replacing it: a score you just earned stays visible behind the
        board that score just moved you up.
      */}
      {hash === '#leaderboard' && (
        <Leaderboard
          player={player}
          onSignIn={() => setPending({ kind: 'signin' })}
          onClose={closeLeaderboard}
        />
      )}
    </>
  );
}
